/**
 * Retângulo de recorte, em % das dimensões naturais da imagem — a mesma
 * convenção de medição que `useMaskDraw` já usa para as suas marcações.
 * Produzido por `useCropBox` (irmão de `useMaskDraw`, design Decisão 8); esta
 * função não sabe nada sobre arrasto ou DOM visível, só consome o resultado.
 */
export interface CaixaDeRecorte {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Tamanho de saída pedido, em px. Pode exceder o recorte natural — ver
 *  `recortarEReduzirImagem`, que nunca amplia além dele. */
export interface TamanhoDestino {
  width: number;
  height: number;
}

/** Exportado para o alvo de arquivo (`AlvoDeImagemDaFace`) validar e montar o
 *  atributo `accept` a partir da mesma lista — nunca uma segunda cópia capaz
 *  de divergir desta. */
export const TIPOS_DE_IMAGEM_ACEITOS = ['image/png', 'image/jpeg'] as const;
export type TipoDeImagemAceito = (typeof TIPOS_DE_IMAGEM_ACEITOS)[number];

export function tipoDeImagemAceito(type: string): type is TipoDeImagemAceito {
  return (TIPOS_DE_IMAGEM_ACEITOS as readonly string[]).includes(type);
}

/**
 * Recorta a `caixa` (% da imagem) e reduz para `destino`, desenhando num
 * `<canvas>` do tamanho de destino e exportando no mesmo tipo da entrada —
 * a API aceita `application/pdf`, `image/png` e `image/jpeg`; aqui só os
 * dois últimos interessam (design, Decisão 8). Sem biblioteca de recorte:
 * a conta inteira é um `drawImage` seguido de `toBlob`.
 *
 * Pedir um `destino` maior que o recorte natural nunca amplia a imagem:
 * cada eixo é limitado ao próprio tamanho natural do recorte, não só ao
 * tamanho original da imagem — um recorte pequeno numa imagem grande não
 * pode "emprestar" os pixels que já ficaram de fora da caixa.
 *
 * Módulo puro — sem React, sem upload. Quem envia o `File` resultante é a
 * tarefa seguinte (`FileService.upload(file, 'review_image')`).
 */
export async function recortarEReduzirImagem(
  origem: File,
  caixa: CaixaDeRecorte,
  destino: TamanhoDestino,
): Promise<File> {
  if (!tipoDeImagemAceito(origem.type)) {
    throw new Error(
      `Tipo de imagem não aceito: "${origem.type || 'desconhecido'}". Envie PNG ou JPEG.`,
    );
  }

  const bitmap = await createImageBitmap(origem);
  try {
    const sx = (caixa.x / 100) * bitmap.width;
    const sy = (caixa.y / 100) * bitmap.height;
    const sw = Math.max(1, (caixa.w / 100) * bitmap.width);
    const sh = Math.max(1, (caixa.h / 100) * bitmap.height);

    // Nunca amplia: o destino pedido nunca passa do tamanho natural do recorte.
    const destW = Math.max(1, Math.min(Math.round(destino.width), Math.round(sw)));
    const destH = Math.max(1, Math.min(Math.round(destino.height), Math.round(sh)));

    const canvas = document.createElement('canvas');
    canvas.width = destW;
    canvas.height = destH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D indisponível neste navegador.');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, destW, destH);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, origem.type),
    );
    if (!blob) throw new Error('Falha ao gerar o arquivo recortado.');

    const extensao = origem.type === 'image/png' ? 'png' : 'jpg';
    const nome = `${origem.name.replace(/\.\w+$/, '') || 'imagem'}.${extensao}`;
    return new File([blob], nome, { type: origem.type });
  } finally {
    bitmap.close();
  }
}
